import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@vimaana/ui";

const BASE = "/api/pflege/vertraege";

/** Ein Vertrag, wie er an einem Kunden hängt (schlank, aus `GET /kundenstamm`). */
export type KundenVertrag = {
  id: string; nummer: string; status: string; beginn: string | null;
  jahres_netto: number | string | null; monats_netto: number | string | null;
  vertragsart: string; version: number; gekuendigt_zum: string | null; zahlweise: string;
};

/** Ein Pflegekunde: mit Vertrag oder mit Pflegemonaten in Kontor. */
export type PflegeKunde = {
  id: string; name: string; kundentyp: string;
  strasse: string; plz: string; ort: string; lieferadresse: string;
  telefon: string; email: string;
  pflege_monate_json: string | null;
  /** Neueste zuerst. */
  vertraege: KundenVertrag[];
};

export type Rate = {
  id: string; periode: string;
  soll_netto: string; soll_brutto: string | null;
  /** Stand der Rate: offen, entwurf_erzeugt, versendet, bezahlt, storniert. */
  status: string;
  rechnung_nummer: string | null;
  /** Letzter Lastschrift-Versuch: eingereicht, bezahlt, zurueck, storniert — null ohne Einzug. */
  einzug_status: string | null;
  einzug_am: string | null;
  zurueck_grund: string | null;
  einzug_weg: string | null;
};

export function usePflegekunden() {
  return useQuery<PflegeKunde[]>({
    queryKey: ["pflege", "kundenstamm"],
    queryFn: () => apiGet<PflegeKunde[]>(`${BASE}/kundenstamm`),
    staleTime: 60_000,
  });
}

export function useRaten(vertragId: string | undefined) {
  return useQuery<Rate[]>({
    queryKey: ["pflege", "raten", vertragId],
    queryFn: () => apiGet<Rate[]>(`${BASE}/${vertragId}/raten`),
    enabled: !!vertragId,
  });
}

/** Wo ein Kunde steht — dieselben Stufen wie die Filter auf der Kundenseite. */
export type Stufe = "interessent" | "angebot" | "laufend" | "kuendigung" | "ehemalig";

export const STUFE_LABEL: Record<Stufe, string> = {
  interessent: "Interessent", angebot: "Im Angebot", laufend: "Laufend",
  kuendigung: "Kündigung läuft", ehemalig: "Ehemalig",
};
export const STUFE_TON: Record<Stufe, string> = {
  interessent: "", angebot: "is-blau", laufend: "is-gruen", kuendigung: "is-rot", ehemalig: "",
};

/** Der Vertrag, um den es gerade geht: der neueste, der nicht beendet ist. */
export function aktuellerVertrag(k: PflegeKunde): KundenVertrag | null {
  return k.vertraege.find(v => v.status !== "beendet") ?? null;
}

export function stufe(k: PflegeKunde): Stufe {
  const v = aktuellerVertrag(k);
  if (!v) return k.vertraege.length ? "ehemalig" : "interessent";
  if (["entwurf", "angeboten", "zugestimmt"].includes(v.status)) return "angebot";
  if (v.status === "gekuendigt") return "kuendigung";
  return "laufend";
}

const MONAT_KURZ = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** Pflegemonate aus Kontor: [[3, "Platanenschnitt"], [10, "Kirsche"]]. */
export function pflegeMonate(k: { pflege_monate_json: string | null }): [number, string][] {
  if (!k.pflege_monate_json) return [];
  try {
    const o = JSON.parse(k.pflege_monate_json) as Record<string, string>;
    return Object.entries(o).map(([m, t]) => [Number(m), (t || "").trim()] as [number, string])
      .filter(([m]) => m >= 1 && m <= 12).sort((a, b) => a[0] - b[0]);
  } catch { return []; }
}
export const monatKurz = (m: number) => MONAT_KURZ[m - 1] ?? "";

export function anschrift(k: PflegeKunde): string {
  const zeile = [k.strasse, [k.plz, k.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return zeile || k.lieferadresse.replace(/\n/g, ", ");
}
